import React, { useEffect, useState } from 'react';
import styled from 'styled-components/macro';
import { InputBase } from '@material-ui/core';
import debounce from 'lodash.debounce';
// import { Scrollbars } from 'react-custom-scrollbars';

const Wrapper = styled.div`
  position: relative;
  text-align: initial;
  z-index: 0;
  overflow-x: hidden;
  overflow-y: auto;
  max-height: 20vh;

  [class*='MuiInputBase-root'],
  .behind {
    padding: 1em;
    line-height: 1.75em;
    font-size: 14px;
    font-family: ${({ theme }) => theme.typography.fontFamily};
    width: 100%;
    max-width: 100%;
    overflow: hidden;
  }

  [class*='MuiInputBase-root'] {
    width: 100%;
    overflow: hidden;
    z-index: 1;
  }

  .behind {
    position: absolute;
    top: 0;
    left: -0.25em;
    right: 0;
    width: auto;
    color: #ccc;
    z-index: 0;
    pointer-events: none;
    padding-left: 1.25em;

    span {
      color: transparent;
      visibility: hidden;
    }
  }

  .behind *,
  textarea {
    word-break: break-word;
    white-space: pre-wrap;
  }

  .behind__line:before {
    content: '▪';
    display: block;
    position: absolute;
    left: 0;
    width: 1em;
    text-align: center;
    color: steelblue;
  }
`;

export default function ListBuilder({
  onChange,
  value: controlledValue,
  maxHeight,
  emojiTargetRef,
  htmlId,
  dontUpdateValue,
  ...props
}) {
  const inputRef = React.useRef(null);

  const [value, setValue] = React.useState(controlledValue || '');

  useEffect(() => {
    if (dontUpdateValue) {
      return;
    }

    let cLines = [],
      vLines = [];
    try {
      if (controlledValue !== value) {
        cLines = controlledValue.split('\n');
        vLines = value.split('\n');
        if (cLines.length === vLines.length) {
          setValue(controlledValue);
        }
      }
    } catch (e) {}
  }, [controlledValue]);

  const ensureNewline = React.useCallback(
    // debounce to avoid moving caret while typing
    debounce(() => {
      const element = inputRef.current;

      if (!element) {
        return;
      }

      const v = element.value;

      if (v[v.length - 1] !== '\n') {
        let caret = element.selectionStart;

        setValue(v.replace(/([^\n])\n*$/, '$1\n'));

        if (element && caret && document.activeElement === element) {
          window.requestAnimationFrame(() => {
            if (element && document.activeElement === element) {
              element.selectionStart = caret;
              element.selectionEnd = caret;
            }
          });
        }
      }

      // there is still a risk of a keystroke in the window between [delay] and [delay + next frame], so give a generous delay to make the window harder to hit
    }, 500),
    []
  );

  React.useEffect(() => {
    ensureNewline();

    // all options empty, clear it
    if (/^\s+$/.test(value) && value !== '') {
      setValue('');
    }
  }, [ensureNewline, value]);

  const lines = value.split('\n');

  const placeholder =
    lines.length === 1 ? 'Paste or type options here' : 'Add an option';

  return (
    // <Scrollbars autoHeight autoHeightMax={300}>
    <Wrapper maxHeight={maxHeight}>
      <div className="behind">
        {lines.map((line, i) => {
          return (
            <div className="behind__line" key={i}>
              {line ? <span>{line}</span> : placeholder} &nbsp;
            </div>
          );
        })}
      </div>
      <InputBase
        multiline
        inputProps={{ 'data-testid': `option-builder-${htmlId}` }}
        inputRef={inputRef}
        onFocus={e => {
          emojiTargetRef.current = e.target;
        }}
        onChange={e => {
          let options;

          if (!dontUpdateValue) {
            options = e.target.value;
            setValue(options);
          } else {
            options = value;
          }
          onChange(options.trim());
        }}
        value={value}
        {...props}
        disabled={false}
      />
    </Wrapper>
    // </Scrollbars>
  );
}
